import {createSelector} from '@reduxjs/toolkit'

import {join, findIndex} from '@shared/helpers/array'
import {find} from '@shared/helpers/collection'
import {flow} from '@shared/helpers/function'
import {eq, gt, isEmpty, validateMobileNumber} from '@shared/helpers/lang'
import {prop} from '@shared/helpers/object'

import {
    AccountPayInfo,
    BillingPayMethodsKey,
    BillingPayMethodsKeyType,
    MobilePGChannel,
    PayMethod,
    PayPartnershipItemCode,
    UNAVAILABLE_REASON_TYPE,
} from '@web/core/constants'

import {CardEasyPay, RootState} from '../interfaces'
import {isMobiliansPayMethod, sortCardEasyPaysItems, splitDateTimeToDateAndTime} from '../utils'

export const priorityPaymentMethodSelector = (state: RootState) => state.priorityPaymentMethod

/** 선택된 결제수단 */
export const selectedMethodSelector = (state: RootState) => state.priorityPaymentMethod.selectedPayment.selectedMethod

/** 이전에 선택된 결제 수단 */
export const prevSelectedMethodSelector = (state: RootState) =>
    state.priorityPaymentMethod.selectedPayment.prevSelectedMethod

/** 충전계좌 원본 목록 */
const chargeAccountsSelector = (state: RootState) => state.priorityPaymentMethod.payMethod.chargedAccounts
/** 선택된 인출(충전) 계좌 Key */
export const selectedWithdrawalAccountKeySelector = (state: RootState) =>
    state.priorityPaymentMethod.selectedPayment.chargeAccount.accountKey

/**
 * 충전결제 계좌목록
 * - 등록이 완료되지 않은 계좌는 필터링 합니다.
 * - 원본 목록을 기준으로 새로운 속성을 가진 목록을 반환합니다.
 * - 충전계좌 목록 기준 Selector
 */
export const withdrawalAccountsSelector = createSelector(
    chargeAccountsSelector,
    selectedWithdrawalAccountKeySelector,
    (withdrawalAccounts, selectedWithdrawalAccountKey) =>
        withdrawalAccounts
            .filter(flow(prop('registrationCompletedDateTime'), isEmpty, eq(false)))
            .map(
                ({
                    accountKey: id,
                    bankCode: code,
                    bankName,
                    maskedAccountNumber: number,
                    payPartnershipItemCode: partnershipCode,
                    payable,
                    invalidReason,
                }) => {
                    return {
                        id,
                        code,
                        number,
                        isNaverAccount: eq(partnershipCode, PayPartnershipItemCode.MIRAEASSET_CMA),
                        bankName,
                        partnershipCode,
                        isUnderMaintenance: !payable,
                        selected: eq(id, selectedWithdrawalAccountKey),
                        invalidReason,
                    }
                },
            ),
)

/** 충전 계좌 보유 여부 */
export const hasWithdrawalAccountSelector = createSelector(withdrawalAccountsSelector, ({length}) => gt(length, 0))

/** 선택된 충전계좌 정보 */
export const selectedWithdrawalAccountSelector = createSelector(
    withdrawalAccountsSelector,
    find(({selected}) => selected),
)

/** 선택한 충전계좌가 네이버 통장인지 여부 */
export const naverBankbookOpenedSelector = createSelector(
    selectedWithdrawalAccountSelector,
    (selectedAccount) => !!selectedAccount?.isNaverAccount,
)

/** 선택한 충전계좌로 충전가능 여부 Selector */
export const availableAddingRechargePointSelector = createSelector(
    selectedWithdrawalAccountSelector,
    (selectedAccount) => !(selectedAccount?.isUnderMaintenance ?? true),
)

export const easyPayAccountsSelector = createSelector(
    (state: RootState) => state.priorityPaymentMethod.payMethod.bankEasyPays,
    (bankEasyPays) => {
        return bankEasyPays.filter(flow(prop('registrationCompletedDateTime'), isEmpty, eq(false))).map((account) => {
            const {payable, maintenanceSchedule} = account
            const status = payable ? AccountPayInfo.PAY_AVAILABLE : AccountPayInfo.PAY_UNABLE
            const {date: startDate, time: startTime} = splitDateTimeToDateAndTime(
                maintenanceSchedule?.startDateTime ?? '',
            )
            const {date: endDate, time: endTime} = splitDateTimeToDateAndTime(maintenanceSchedule?.endDateTime ?? '')

            return {
                ...account,
                pendingPayable: false,
                payServiceMaintenanceSchedule: maintenanceSchedule && {
                    startDate,
                    startTime,
                    endTime,
                    endDate,
                    message: '',
                },
                payInfo: {
                    status,
                    isPayable: payable,
                    isPendingPayable: false,
                    isMaintenance: !!maintenanceSchedule,
                },
            }
        })
    },
)

/** 선택된 간편결제 계좌 Key */
export const selectedEasyBankAccountKeySelector = (state: RootState) =>
    state.priorityPaymentMethod.selectedPayment.bankEasyPay.accountKey

/** 선택된 간편결제 계좌 정보 */
export const selectedEasyBankAccountSelector = createSelector(
    easyPayAccountsSelector,
    selectedEasyBankAccountKeySelector,
    (bankEasyPays, selectedAccountKey) => {
        return find(({accountKey}) => eq(selectedAccountKey, accountKey), bankEasyPays)
    },
)

/** 선택된 간편 계좌 index */
export const selectedEasyBankIndexSelector = createSelector(
    easyPayAccountsSelector,
    selectedEasyBankAccountKeySelector,
    (bankEasyPays, selectedEasyBankAccount) => {
        const eqSelectedEasyBankAccount = eq(selectedEasyBankAccount)
        // 간편 계좌 결제 추가 일 경우
        if (!selectedEasyBankAccount) {
            return bankEasyPays?.length ?? 0
        }
        const index = findIndex(({accountKey}) => eqSelectedEasyBankAccount(accountKey), bankEasyPays)

        return Math.max(index, 0)
    },
)

/** 간편 계좌결제 등록 여부 (간편 계좌결제 api 조회 성공 && 계좌목록 보유) */
export const isRegisteredEasyPayBankSelector = createSelector(
    easyPayAccountsSelector,
    (easyPayAccounts) => !isEmpty(easyPayAccounts),
)

/** 간편결제 카드 목록 */
export const easyPayCardsSelector = createSelector(
    (state: RootState) => state.priorityPaymentMethod.payMethod.cardEasyPays,
    sortCardEasyPaysItems<CardEasyPay>(({payServiceAvailable}) => payServiceAvailable),
)

/** 선택된 간편결제 카드 Id */
export const selectedEasyCardIdSelector = (state: RootState) =>
    state.priorityPaymentMethod.selectedPayment.cardEasyPay.cardId

/** 선택된 간편결제 카드 정보 */
export const selectedEasyCardSelector = createSelector(
    easyPayCardsSelector,
    selectedEasyCardIdSelector,
    (cardEasyPays, selectedCardId) => find(({cardId}) => eq(selectedCardId, cardId), cardEasyPays),
)

export const selectedEasyCardIndexSelector = createSelector(
    easyPayCardsSelector,
    selectedEasyCardIdSelector,
    (cardEasyPays, selectedEasyCardId) => {
        const eqSelectedEasyCardId = eq(selectedEasyCardId)
        // 간편 카드 결제 추가 일 경우
        if (isEmpty(selectedEasyCardId)) {
            return cardEasyPays?.length ?? 0
        }
        const index = findIndex(({cardId}) => eqSelectedEasyCardId(cardId), cardEasyPays)
        return index > -1 ? index : cardEasyPays.length
    },
)

/** 간편 카드결제 등록 여부 */
export const isRegisteredEasyPayCreditCardSelector = createSelector(
    easyPayCardsSelector,
    (easyPaysCards) => !isEmpty(easyPaysCards),
)

/** 입력된 휴대폰 정보 */
export const selectedPaymentMobileSelector = (state: RootState) => state.priorityPaymentMethod.selectedPayment.mobile

/** 일반결제 휴대폰 번호 유효성 체크 통과 여부 */
export const isMobileNumberValidSelector = createSelector(
    selectedPaymentMobileSelector,
    flow(prop('mobileNumbers'), join(''), validateMobileNumber),
)

/** 선택된 billingPayMethod 키 값과 개별 타입과 일치여부 속성을 함께 반환 */
export const selectedBillingPayMethodInfoSelector = createSelector(
    (state: RootState) => state.priorityPaymentMethod.selectedPayment.billingPayMethod,
    (selectedBillingPayMethodKey) => {
        const selectedBillingPayMethodKeyEqualsTo: (key: BillingPayMethodsKeyType) => boolean =
            eq(selectedBillingPayMethodKey)
        return {
            key: selectedBillingPayMethodKey,
            isBankEasyPay: selectedBillingPayMethodKeyEqualsTo(BillingPayMethodsKey.BANK_EASY_PAY),
            isCardEasyPay: selectedBillingPayMethodKeyEqualsTo(BillingPayMethodsKey.CARD_EASY_PAY),
            isMobile: selectedBillingPayMethodKeyEqualsTo(BillingPayMethodsKey.MOBILE),
            isRecharge: selectedBillingPayMethodKeyEqualsTo(BillingPayMethodsKey.RECHARGE_POINT),
        }
    },
)

/** 선택된 결제수단에 따라 billingPayMethod 를 반환 */
export const selectedBillingPayMethodsSelector = createSelector(
    priorityPaymentMethodSelector,
    selectedBillingPayMethodInfoSelector,
    ({payMethod: {billingPayMethods}}, {key: billingPayMethodKey}) => {
        return billingPayMethodKey ? billingPayMethods[billingPayMethodKey] : null
    },
)

/** 휴대폰 일반결제 Billing 정보를 반환 */
export const mobileBillingPayMethodSelector = createSelector(
    (state: RootState) => state.priorityPaymentMethod.payMethod.billingPayMethods.mobile,
    (mobileBillingPayMethod) => {
        const pgChannel = mobileBillingPayMethod.pgName
        const pgChannelEqualsTo = eq(pgChannel)

        return {
            ...mobileBillingPayMethod,
            ...(isMobiliansPayMethod(mobileBillingPayMethod)
                ? {
                      sId: mobileBillingPayMethod.sId,
                      svcId: mobileBillingPayMethod.svcId,
                      cancelUri: mobileBillingPayMethod.cancelUri,
                      failUri: mobileBillingPayMethod.failUri,
                      successUri: mobileBillingPayMethod.successUri,
                  }
                : null),
            pgChannel,
            isDanal: pgChannelEqualsTo(MobilePGChannel.DANAL),
            isMobilians: pgChannelEqualsTo(MobilePGChannel.MOBILIANS),
        }
    },
)

/** 2차 결제수단 */

/** 선택된 2순위 결제수단 */
export const selectedSecondPayMethodSelector = (state: RootState) =>
    state.priorityPaymentMethod.selectedSecondPayment.selectedMethod

/** 2차 결제수단 계좌간편 목록 */
export const secondEasyPayAccountsSelector = createSelector(
    easyPayAccountsSelector,
    selectedEasyBankAccountKeySelector,
    selectedMethodSelector,
    selectedSecondPayMethodSelector,
    (bankEasyPays, selectedAccountKey, selectedMethod, selectedSecondMethod) => {
        const isSelectedDuplicatedPayment =
            selectedMethod === PayMethod.BANK_EASY_PAY && selectedSecondMethod === PayMethod.BANK_EASY_PAY
        return bankEasyPays.map((account) => {
            return {
                ...account,
                ...(account.accountKey === selectedAccountKey &&
                    isSelectedDuplicatedPayment && {
                        unavailableReason: UNAVAILABLE_REASON_TYPE.DUPLICATION,
                    }),
            }
        })
    },
)

/** 2차 결제수단 카드간편 목록 */
export const secondEasyPayCardsSelector = createSelector(
    easyPayCardsSelector,
    selectedEasyCardIdSelector,
    selectedMethodSelector,
    selectedSecondPayMethodSelector,
    (cardEasyPays, selectedCardId, selectedMethod, selectedSecondMethod) => {
        const isSelectedDuplicatedPayment =
            selectedMethod === PayMethod.CARD_EASY_PAY && selectedSecondMethod === PayMethod.CARD_EASY_PAY
        return cardEasyPays.map((card) => {
            return {
                ...card,
                ...(card.cardId === selectedCardId &&
                    isSelectedDuplicatedPayment && {
                        unavailableReason: UNAVAILABLE_REASON_TYPE.DUPLICATION,
                    }),
            }
        })
    },
)

/** 선택된 2순위 충전결제 계좌 Key */
export const selectedSecondWithdrawalAccountKeySelector = (state: RootState) =>
    state.priorityPaymentMethod.selectedSecondPayment.chargeAccount.accountKey

export const secondWithdrawalAccountsSelector = createSelector(
    chargeAccountsSelector,
    selectedSecondWithdrawalAccountKeySelector,
    (withdrawalAccounts, selectedWithdrawalAccountKey) =>
        withdrawalAccounts
            .filter(flow(prop('registrationCompletedDateTime'), isEmpty, eq(false)))
            .map(
                ({
                    accountKey: id,
                    bankCode: code,
                    bankName,
                    maskedAccountNumber: number,
                    payPartnershipItemCode: partnershipCode,
                    payable,
                    invalidReason,
                }) => {
                    return {
                        id,
                        code,
                        number,
                        isNaverAccount: eq(partnershipCode, PayPartnershipItemCode.MIRAEASSET_CMA),
                        bankName,
                        partnershipCode,
                        isUnderMaintenance: !payable,
                        selected: eq(id, selectedWithdrawalAccountKey),
                        invalidReason,
                    }
                },
            ),
)

/** 선택된 충전계좌 정보 */
export const selectedSecondWithdrawalAccountSelector = createSelector(
    secondWithdrawalAccountsSelector,
    find(({selected}) => selected),
)

/** 선택된 2순위 간편결제 계좌 Key */
export const selectedSecondEasyBankAccountKeySelector = (state: RootState) =>
    state.priorityPaymentMethod.selectedSecondPayment.bankEasyPay.accountKey

/** 선택된 2순위간편결제 카드 Id */
export const selectedSecondEasyCardIdSelector = (state: RootState) =>
    state.priorityPaymentMethod.selectedSecondPayment.cardEasyPay.cardId

/** 선택된 2순위 간편결제 계좌 정보 */
export const selectedSecondEasyBankAccountSelector = createSelector(
    easyPayAccountsSelector,
    selectedSecondEasyBankAccountKeySelector,
    (bankEasyPays, selectedAccountKey) => {
        return find(({accountKey}) => eq(selectedAccountKey, accountKey), bankEasyPays)
    },
)

/** 선택된 2차 간편 계좌 index */
export const selectedSecondEasyBankIndexSelector = createSelector(
    secondEasyPayAccountsSelector,
    selectedSecondEasyBankAccountKeySelector,
    (bankEasyPays, selectedEasyBankAccount) => {
        const eqSelectedEasyBankAccount = eq(selectedEasyBankAccount)
        // 간편 계좌 결제 추가 플레이트 일 경우
        if (!selectedEasyBankAccount) {
            return bankEasyPays?.length ?? 0
        }
        const index = findIndex(({accountKey}) => eqSelectedEasyBankAccount(accountKey), bankEasyPays)

        return Math.max(index, 0)
    },
)

/**
 * 선택된 2차 간편 카드 index
 */
export const selectedSecondEasyCardIndexSelector = createSelector(
    secondEasyPayCardsSelector,
    selectedSecondEasyCardIdSelector,
    (cardEasyPays, selectedEasyCardId) => {
        const eqSelectedEasyCardId = eq(selectedEasyCardId)
        // 간편 카드 결제 추가 일 경우
        if (isEmpty(selectedEasyCardId)) {
            return cardEasyPays?.length ?? 0
        }
        const index = findIndex(({cardId}) => eqSelectedEasyCardId(cardId), cardEasyPays)
        return index > -1 ? index : 0
    },
)

/** 선택된 2순위 간편결제 카드 정보 */
export const selectedSecondEasyCardSelector = createSelector(
    easyPayCardsSelector,
    selectedSecondEasyCardIdSelector,
    (cardEasyPays, selectedCardId) => find(({cardId}) => eq(selectedCardId, cardId), cardEasyPays),
)

/** 사용 가능한 결제 수단 타입 */
export const availablePayMethodTypesSelector = (state: RootState) =>
    state.priorityPaymentMethod.payMethod.availablePayMethodTypes

export const isDuplicateAccountSelector = createSelector(
    secondEasyPayAccountsSelector,
    selectedSecondEasyBankAccountKeySelector,
    (accounts, selectedAccountKey) =>
        find(({accountKey}) => selectedAccountKey === accountKey, accounts)?.unavailableReason ===
        UNAVAILABLE_REASON_TYPE.DUPLICATION,
)

export const isDuplicateCardSelector = createSelector(
    secondEasyPayCardsSelector,
    selectedSecondEasyCardIdSelector,
    (cards, selectedCardId) =>
        find(({cardId}) => selectedCardId === cardId, cards)?.unavailableReason === UNAVAILABLE_REASON_TYPE.DUPLICATION,
)
